import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SPFT_URL } from '@insite-store360/shared';
import { Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';

export interface SpftRetailer {
  id: number;
  name: string;
  countryId: number;
  status?: boolean;
}

export interface SpftCreateUserPayload {
  firstName: string;
  lastName: string;
  userId: string;
  roleId: number;
  status?: boolean;
  regionId?: number;
  countryIds?: number[];
  retailerIds: number[];
}

/** SPFT user detail (`GET /users/:id`) — source of assigned countries/retailers on edit. */
export interface SpftUserDetail {
  id: number;
  roleId: number | null;
  regionId: number | null;
  countryIds: number[];
  retailerIds: number[];
  /** Retailers with country so edit can derive countries when SSO has none. */
  retailers: Array<{ id: number; name: string; countryId: number }>;
}

export type SpftUpdateUserPayload = Partial<SpftCreateUserPayload>;

/**
 * Client for SPFT-service user/retailer APIs used by the common Add User page
 * when the selected project is SPFT (projectId = 4).
 */
@Injectable({
  providedIn: 'root',
})
export class SpftUserService {
  private readonly baseUrl = SPFT_URL;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  listRetailers(params?: {
    page?: number;
    limit?: number;
    status?: boolean;
    countryId?: number;
    search?: string;
    /** When true, return all retailers (needed for user create/assign). */
    ignoreUserAssignment?: boolean;
  }): Observable<{ data: SpftRetailer[]; totalPages: number }> {
    let query = new HttpParams()
      .set('page', String(params?.page ?? 1))
      .set('limit', String(params?.limit ?? 100));
    if (params?.status !== undefined) {
      query = query.set('status', String(params.status));
    }
    if (params?.countryId != null) {
      query = query.set('countryId', String(params.countryId));
    }
    if (params?.search?.trim()) {
      query = query.set('search', params.search.trim());
    }
    if (params?.ignoreUserAssignment) {
      query = query.set('ignoreUserAssignment', 'true');
    }

    return this.http.get<any>(`${this.baseUrl}/retailers`, { params: query }).pipe(
      map((res) => {
        const envelope = res?.result ?? res;
        const data = Array.isArray(envelope?.data)
          ? envelope.data
          : Array.isArray(envelope?.result)
            ? envelope.result
            : Array.isArray(envelope)
              ? envelope
              : [];
        const totalPages = Number(
          envelope?.pagination?.totalPages ?? envelope?.totalPages ?? 1
        );
        return {
          data: data
            .map((r: any) => ({
              id: Number(r.id),
              name: String(r.name ?? ''),
              countryId: Number(r.countryId ?? r.country?.id ?? 0),
              status: r.status !== false,
            }))
            .filter((r: SpftRetailer) => r.id && r.name),
          totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
        };
      }),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * Load all active retailers for SPFT user create/assign.
   * Uses ignoreUserAssignment so IntElux admins see the full retailer list
   * (same as SPFT create-user-modal), not only retailers assigned to themselves.
   */
  listAllActiveRetailers(): Observable<SpftRetailer[]> {
    const pageSize = 1000;
    const listParams = {
      page: 1,
      limit: pageSize,
      status: true,
      ignoreUserAssignment: true,
    };
    return this.listRetailers(listParams).pipe(
      expand(({ totalPages }, index) => {
        const nextPage = index + 2;
        return nextPage <= totalPages
          ? this.listRetailers({ ...listParams, page: nextPage })
          : of();
      }),
      map(({ data }) => data),
      reduce((acc, pageData) => [...acc, ...pageData], [] as SpftRetailer[]),
      catchError(this.errorHandler.handleError)
    );
  }

  createUser(payload: SpftCreateUserPayload): Observable<unknown> {
    return this.http
      .post<unknown>(`${this.baseUrl}/users`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * SPFT user detail for edit — retailer/country assignments live in
   * spft-service (mapping.retailer_users), not in the SSO role payload.
   * Returns null instead of erroring so edit still opens without SPFT data.
   */
  getUser(userId: number): Observable<SpftUserDetail | null> {
    return this.http.get<any>(`${this.baseUrl}/users/${userId}`).pipe(
      map((res) => {
        // spft-service GlobalResponseInterceptor wraps as `{ data }`;
        // some gateways also use `{ result }` — accept both.
        const user = res?.data ?? res?.result ?? res;
        if (user?.id == null) {
          return null;
        }
        const retailers = Array.isArray(user.retailers)
          ? user.retailers
              .map((r: any) => ({
                id: Number(r?.id),
                name: String(r?.name ?? ''),
                countryId: Number(r?.countryId ?? r?.country?.id ?? 0),
              }))
              .filter((r: { id: number; name: string; countryId: number }) => !!r.id)
          : [];
        const retailerIds = retailers.map(
          (r: { id: number; name: string; countryId: number }) => r.id
        );
        let countryIds = Array.isArray(user.countries)
          ? user.countries
              .map((c: any) => Number(c?.id))
              .filter((id: number) => !!id)
          : [];
        // Prefer countries from retailer mappings when SSO/mv list has none
        if (
          !countryIds.length &&
          retailers.some(
            (r: { id: number; name: string; countryId: number }) => r.countryId
          )
        ) {
          countryIds = [
            ...new Set(
              retailers
                .map(
                  (r: { id: number; name: string; countryId: number }) =>
                    r.countryId
                )
                .filter((id: number) => !!id)
            ),
          ];
        }
        return {
          id: Number(user.id),
          roleId: user.roleId != null ? Number(user.roleId) : null,
          regionId: user.regionId != null ? Number(user.regionId) : null,
          countryIds,
          retailerIds,
          retailers,
        };
      }),
      catchError(() => of(null))
    );
  }

  updateUser(
    userId: number,
    payload: SpftUpdateUserPayload
  ): Observable<unknown> {
    return this.http
      .patch<unknown>(`${this.baseUrl}/users/${userId}`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }
}
